import { CommonModule, DatePipe } from '@angular/common';
import { AfterViewInit, Component, inject, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { finalize } from 'rxjs/operators';
import { PrmsOrderSummary } from '../../model/prms-order-summary';
import { PrmsOrdersSearchService } from '../../services/prms-order-search-service';
import { StringUtils } from '../../utils/string-utils';
import { PrmsOrdersSearchDetailsComponent } from './orders-search-details-dialog.component';

@Component({
  selector: 'app-prms-order-search-dialog',
  templateUrl: './orders-search-dialog.component.html',
  styleUrls: ['./orders-search-dialog.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PrmsOrdersSearchDetailsComponent, CommonModule, ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatIconModule, MatButtonModule, MatTableModule, MatSortModule, MatProgressBarModule, DatePipe, MatSelectModule]
})

export class PrmsOrdersSearchDialogComponent implements AfterViewInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly service = inject(PrmsOrdersSearchService);
  private readonly dialogRef = inject(MatDialogRef<PrmsOrdersSearchDialogComponent>);
  view = signal<'search' | 'details'>('search');
  isBusy = signal(false);
  errorMessage = signal<string | null>(null);
  displayedColumns: (keyof PrmsOrderSummary)[] = ['orderNumber', 'orderType', 'customerNumber', 'customerName', 'status', 'customerPo', 'enteredDate', 'projectName'];
  rows = signal<PrmsOrderSummary[] | null>(null);
  dataSource = new MatTableDataSource<PrmsOrderSummary>([]);
  form = this.formBuilder.group({ searchTerm: this.formBuilder.nonNullable.control<string>('', { validators: [] }) });
  selectedOrder = signal<PrmsOrderSummary | null>(null);
  @ViewChild(MatSort, {static: true}) sort!: MatSort;


  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item: PrmsOrderSummary, property: string) => {
      if (property === 'enteredDate') {
        const t = item?.enteredDate ? new Date(item.enteredDate).getTime() : 0;
        return isNaN(t) ? 0 : t;
      }
      const value = (item as any)?.[property];
      if (typeof value === 'number') return value;
      if (value == null) return '';
      return String(value).toLowerCase();
    };
  }

  private applyRows(rows: PrmsOrderSummary[]) {
    this.rows.set(rows);
    this.dataSource.data = rows;
  }

  close(): void {
    this.dialogRef.close();
  }

  search(): void {
    this.isBusy.set(true);
    this.errorMessage.set(null);
    const { searchTerm } = this.form.getRawValue();
    if (!(searchTerm?.trim())) {
      this.errorMessage.set('Please enter a valid search value.');
      this.applyRows([]);
      this.isBusy.set(false);
      return;
    }
    this.service.getOrders(searchTerm)
      .pipe(finalize(() => this.isBusy.set(false)))
      .subscribe({
        next: data => {
          this.resetSort();
          this.applyRows(data)
        },
        error: async err => {
          console.error(err);
          await this.handleError(err);
          this.applyRows([]);
        }
      });
  }

  private async handleError(error: any) {
    this.errorMessage.set(await StringUtils.getErrorMessageAsync(error));
  }

  private resetSort() {
    if (!this.sort) return;
    this.sort.active = '';
    this.sort.direction = '';
    this.sort.sortChange.emit({ active: '', direction: '' });
    this.dataSource.sort = this.sort;
  }

  openDetails(selectedOrder: PrmsOrderSummary): void {
    this.selectedOrder.set(selectedOrder);
    this.view.set('details');
  }

  backToSearch(): void {
    this.view.set('search');
    this.selectedOrder.set(null);
  }
}
